import React from 'react';
import { Modal, Form, Input, message, Button, InputNumber } from 'antd';
import { useTranslation } from '@refinedev/core';
import useApi from '@/api/useApi';
import { endPoints } from '@/api/configs';

interface AddTopicModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
}

export const AddTopicModal: React.FC<AddTopicModalProps> = ({
  open,
  onCancel,
  refetch,
}) => {
  const { translate } = useTranslation();
  const { usePost } = useApi();
  const { mutate: createTopic, isLoading } = usePost(endPoints.topic());
  const [form] = Form.useForm();

  const handleOk = async () => {
    createTopic(
      {
        name: form.getFieldValue('name'),
      },
      {
        onSuccess: () => {
          form.resetFields();
          message.success(translate('listing-topics.success.add-topic'));
          refetch();
          onCancel();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={translate('listing-topics.add-topic')}
      open={open}
      footer={null}
      onClose={handleCancel}
      onCancel={handleCancel}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          name="name"
          label={translate('listing-topics.name')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="sort_order"
          label={translate('listing-topics.sort-order')}
          rules={[
            {
              validator: (_, value) => {
                if (value === undefined || value === null) {
                  return Promise.reject(
                    new Error(translate('pages.errors.required'))
                  );
                }
                if (value < 1) {
                  return Promise.reject(
                    new Error(translate('pages.errors.positive-integer'))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          className="w-full"
        >
          <InputNumber min={1} className="w-full" />
        </Form.Item>
        <div className="flex justify-end gap-2">
          <Button onClick={handleCancel}>{translate('buttons.cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {translate('buttons.add')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
